import type {StoredFile} from "./storedFiles";

/**
 * "Do this to the home composer once you are there" — React-free. Two asks use it: "Turn the composer to
 * asking for files" (kitecorp/transferify-ui#3140), which the Transfers sheet's Requested tab makes before it
 * moves the tab home, and "Make a new link to these stored files" (kitecorp/transferify-ui#3405), which the
 * Files tab makes the same way and which carries the files it hands over.
 *
 * The request is module memory rather than router state on purpose: it survives exactly the client-side
 * push it is made for (the same document), vanishes with a full navigation, and cannot be replayed by the
 * Back button the way a history entry's state would be.
 *
 * A request expires, so a visitor who took another route to the home page much later is never surprised by
 * a changed composer.
 */
export const COMPOSER_ARRIVAL_REQUEST_TTL_MS = 5000;

export interface ComposerArrivalRequests {
	/** Ask, at `now` (milliseconds). */
	request(now: number): void;
	/** Whether a request is waiting at `now`, spending it if so. */
	consume(now: number): boolean;
}

/** An ask that carries what the composer is handed. Only the latest one waits. */
export interface ComposerArrivalAsks<T> {
	/** Ask, at `now` (milliseconds), handing over `payload`. */
	request(payload: T, now: number): void;
	/** The payload waiting at `now`, spending it, or null when nothing waits. */
	consume(now: number): T | null;
}

export function createComposerArrivalAsks<T>(): ComposerArrivalAsks<T> {
	let waiting: {payload: T; requestedAt: number} | null = null;
	return {
		request(payload, now) {
			waiting = {payload, requestedAt: now};
		},
		consume(now) {
			const owed = waiting !== null && now - waiting.requestedAt <= COMPOSER_ARRIVAL_REQUEST_TTL_MS ? waiting.payload : null;
			waiting = null;
			return owed;
		},
	};
}

export function createComposerArrivalRequests(): ComposerArrivalRequests {
	const asks = createComposerArrivalAsks<true>();
	return {
		request: now => asks.request(true, now),
		consume: now => asks.consume(now) !== null,
	};
}

/** This document's requests for the composer's request mode. */
export const requestModeRequests: ComposerArrivalRequests = createComposerArrivalRequests();

/** This document's asks for a new link to stored files: the files the Files tab selected (`home/storedFiles`). */
export const storedFilesRequests: ComposerArrivalAsks<readonly StoredFile[]> = createComposerArrivalAsks();
